import styled, { css } from 'styled-components';
import Image from '@components/image';
import Text from '@components/text';
import VectorIcon from '@components/svg-icons/vector';
import { generateScales } from '@helpers/generate-media-queries';
import Collapsible from 'react-collapsible';

export const TextAndIcon = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    height: 2.5rem;
    width: 2.5rem;
    min-height: 2.5rem;
    min-width: 2.5rem;
    border-radius: 1.375rem;
    padding: 0;
    justify-content: center;
    transition: transform ${theme.transitions.short};

    > svg {
      width: 1rem;
      height: 1rem;
    }

    @media (max-width: ${theme.breakpoints.md}) {
      height: 2.25rem;
      width: 2.25rem;
      min-height: 2.25rem;
      min-width: 2.25rem;

      > svg {
        width: 0.9375rem;
        height: 0.9375rem;
      }
    }

    @media (max-width: ${theme.breakpoints.sm}) {
      height: 2rem;
      width: 2rem;
      min-height: 2rem;
      min-width: 2rem;

      > svg {
        width: 0.875rem;
        height: 0.875rem;
      }
    }
  `}
`;

export const StyledText = styled(Text)`
  width: 100%;
  margin: 0;
  max-width: 40rem;
`;

export const OpenedContent = styled.div`
  ${({ theme }) => css`
    padding: 1.5rem 0rem;
    align-items: center;
    justify-content: space-between;
    font-size: 1.3rem;
    display: flex;
    font-weight: 700;
    line-height: 1.25rem;
    cursor: pointer;
    column-gap: 4rem;

    @media (max-width: ${theme.breakpoints.md}) {
      column-gap: 2rem;
    }

    @media (max-width: ${theme.breakpoints.sm}) {
      column-gap: 1.5rem;
    }

    ${generateScales(
      'font-size',
      theme.fontSize.small.mobile,
      theme.fontSize.subheading.desktop,
      theme
    )}

    &:hover {
      ${TextAndIcon} {
        transform: scale(1.125);
      }
    }
  `}
`;

export const Content = styled.div`
  ${() => css`
    display: flex;
    align-items: center;
    column-gap: 1rem;
    width: 100%;

    &:hover ${TextAndIcon} {
      transform: scale(1.125);
    }
  `}
`;

export const StyledCollapsible = styled(Collapsible)`
  width: 100%;
`;

export const TitleContainer = styled.div`
  ${({ theme }) => css`
    margin: 0 auto;
    ${generateScales('margin-bottom', '2rem', '4rem', theme)}
    max-width: 40rem;
    width: 100%;
    text-align: center;

    @media (max-width: ${theme.breakpoints.sm}) {
      text-align: left;
    }
  `}
`;

export const StyledTitle = styled(Text)`
  ${({ theme }) => css`
    ${generateScales('margin-bottom', '4rem', '7rem', theme)}
    position: relative;
  `}
`;

export const OpenedSubheading = styled.div`
  ${({ theme }) => css`
    padding: 0 0rem 2.5rem 0;
    max-width: 40rem;
    font-size: 1rem;

    @media (max-width: ${theme.breakpoints.md}) {
      padding-left: 0rem;
      padding-bottom: 2rem;
    }
  `}
`;

export const ListItem = styled.li`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 2.5rem;

    .collapsible-item,
    .Collapsible {
      width: 100%;
    }

    @media (max-width: ${theme.breakpoints.md}) {
      padding: 0 2rem;
      border-radius: 1.5rem;
    }

    @media (max-width: ${theme.breakpoints.sm}) {
      padding: 0 1rem;
      border-radius: 1.25rem;
    }
  `}
`;

interface ListProps {
  condensed?: boolean;
}

export const List = styled.ul<ListProps>`
  ${({ theme, condensed }) => css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    max-width: ${condensed ? '56rem' : '100%'};
    margin: 0 auto;

    ${TextAndIcon} {
      background-color: ${condensed ? theme.colors.primary : theme.colors.secondary};
      color: ${condensed ? theme.colors.secondary : theme.colors.primary};
    }

    ${ListItem} {
      background-color: ${condensed ? theme.colors.light : theme.colors.secondary};
      border-radius: ${condensed ? theme.borderRadius.large : '3rem'};
    }

    ${OpenedSubheading} {
      color: ${condensed ? theme.colors.primary : theme.colors.white};

      &:hover {
        color: ${condensed ? theme.colors.secondary : theme.colors.white};
      }
    }

    ${OpenedContent} {
      color: ${condensed ? theme.colors.primary : theme.colors.white};

      &:hover {
        ${StyledText} {
          color: ${condensed ? theme.colors.secondary : theme.colors.white};
        }

        ${TextAndIcon} {
          background-color: ${theme.colors.secondary};
          color: ${condensed ? theme.colors.primary : theme.colors.white};
        }
      }
    }

    ${Content} {
      color: ${condensed ? theme.colors.primary : theme.colors.white};

      &:hover ${TextAndIcon} {
        background-color: ${condensed ? theme.colors.secondary : theme.colors.primary};
        color: ${condensed ? theme.colors.white : theme.colors.white};
      }
    }

    @media (max-width: ${theme.breakpoints.md}) {
      gap: 0.75rem;
    }

    @media (max-width: ${theme.breakpoints.sm}) {
      gap: 0.5rem;
    }
  `}
`;

export const StyledVector = styled(VectorIcon)<{ isRotated: boolean }>`
  ${({ isRotated }) =>
    isRotated &&
    css`
      transform: rotate(180deg);
    `}
`;

export const StyledImage = styled(Image)`
  ${({ theme }) => css`
    height: 2.75rem;
    width: 2.75rem;
    min-height: 2.75rem;
    min-width: 2.75rem;
    align-self: flex-start;

    @media (max-width: ${theme.breakpoints.sm}) {
      width: 2.5rem;
      height: 2.5rem;
      min-width: 2.5rem;
      min-height: 2.5rem;
    }

    img,
    div,
    picture {
      object-position: center center;
    }
  `}
`;

export const Label = styled.span`
  ${({ theme }) => css`
    ${generateScales('font-size', theme.fontSize.body.mobile, theme.fontSize.body.desktop, theme)}
    color: ${theme.colors.secondary};
    font-weight: 400;
    margin: 0;
    width: fit-content;
    text-wrap: nowrap;

    @media (max-width: ${theme.breakpoints.md}) {
      display: none;
    }
  `}
`;

export const TextWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
`;

export const AccordionItem = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: space-between;
    padding: 1.5rem 0rem;
    font-weight: 500;
    cursor: pointer;
    width: 100%;
    align-items: center;
    column-gap: 4rem;
    color: ${theme.colors.primary};
    ${generateScales(
      'font-size',
      theme.fontSize.body.mobile,
      theme.fontSize.subheading.desktop,
      theme
    )}

    &:hover ${StyledText} {
      /* color: ${theme.colors.secondary}; */
    }

    &:hover ${TextAndIcon} {
      background-color: ${theme.colors.secondary};
      /* color: ${theme.colors.primary}; */
      transform: scale(1.125);
    }

    @media (max-width: ${theme.breakpoints.md}) {
      column-gap: 2rem;
      padding: 1.25rem 0rem;
    }
    @media (max-width: ${theme.breakpoints.sm}) {
      column-gap: 1.5rem;
      padding: 1.25rem 0rem;
    }
  `}
`;
